import {
  Flex,
  Box,
  Button,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuToggle,
  PopoverBody,
  PopoverTrigger,
  PopoverContent,
  Popover,
  Icon,
} from '@tonic-one/react';
import { FilterAddIcon } from '@tonic-one/react-icons';
import { useMemo, useState, useEffect } from 'react';
import {
  FilterKeyword,
  FilterKeywordField,
  FeedbackFilterStatus,
  FeedbackFilterCategory,
  FeedbackResultCategory,
} from '../type';
import { useTranslation } from 'react-i18next';
import FilterTag from './components/FilterTag';

const ADDITIONAL_FILTERS = ['sender', 'recipient', 'reporter'];

interface FilterTag {
  type: 'keyword' | 'status' | 'category' | 'resultCategory';
  field?: FilterKeywordField;
  value: string;
  fieldLabel: string;
  valueLabel: string;
}

interface AddFilterMenuProps {
  keyword: FilterKeyword;
  setKeyword: (field: FilterKeywordField, value: string) => void;
  status?: FeedbackFilterStatus;
  setStatus?: (status: FeedbackFilterStatus) => void;
  category?: FeedbackFilterCategory;
  setCategory?: (category: FeedbackFilterCategory) => void;
  resultCategory?: FeedbackResultCategory;
  setResultCategory?: (resultCategory: FeedbackResultCategory) => void;
  resetSelectionsAndPagination?: () => void;
}

const AddFilterMenu = ({
  keyword,
  setKeyword,
  status,
  setStatus,
  category,
  setCategory,
  resultCategory,
  setResultCategory,
  resetSelectionsAndPagination,
}: AddFilterMenuProps) => {
  const { t } = useTranslation(['operations', 'common']);

  // Build list of active filter tags
  const filterTags = useMemo(() => {
    const tags: FilterTag[] = [];

    // Add keyword filters
    (ADDITIONAL_FILTERS as FilterKeywordField[]).forEach((field) => {
      if (keyword[field] && keyword[field].trim() !== '') {
        tags.push({
          type: 'keyword',
          field,
          value: keyword[field],
          fieldLabel: t(`operations.euq.detail.table.header.${field}`),
          valueLabel: keyword[field],
        });
      }
    });

    // Add status filter if not "All"
    if (status && status !== FeedbackFilterStatus.All) {
      const statusLabels: Record<FeedbackFilterStatus, string> = {
        [FeedbackFilterStatus.All]: t('operations.euq.reportAs.type.all'),
        [FeedbackFilterStatus.Created]: t('operations.euq.statusTab.created'),
        [FeedbackFilterStatus.Approved]: t('operations.euq.statusTab.approved'),
        [FeedbackFilterStatus.Rejected]: t('operations.euq.statusTab.rejected'),
        [FeedbackFilterStatus.Bypassed]: t('operations.euq.statusTab.bypassed'),
      };
      tags.push({
        type: 'status',
        value: status,
        fieldLabel: t('operations.euq.detail.table.header.approvalStatus'),
        valueLabel: statusLabels[status],
      });
    }

    // Add category filter if not "All"
    if (category && category !== FeedbackFilterCategory.All) {
      const categoryLabels: Record<FeedbackFilterCategory, string> = {
        [FeedbackFilterCategory.All]: t('operations.euq.reportAs.type.all'),
        [FeedbackFilterCategory.Spam]: t('operations.euq.reportAs.type.spam'),
        [FeedbackFilterCategory.Phishing]: t(
          'operations.euq.reportAs.type.phishing'
        ),
        [FeedbackFilterCategory.NotSpam]: t(
          'operations.euq.reportAs.type.noRisk'
        ),
      };
      tags.push({
        type: 'category',
        value: category,
        fieldLabel: t('operations.euq.filter.reportedType'),
        valueLabel: categoryLabels[category],
      });
    }

    // Add result category filter if not "All"
    if (resultCategory && resultCategory !== FeedbackResultCategory.All) {
      const resultLabels: Record<FeedbackResultCategory, string> = {
        [FeedbackResultCategory.All]: t('operations.euq.reportAs.type.all'),
        [FeedbackResultCategory.Spam]: t(
          'operations.euq.analysis.completedSpam'
        ),
        [FeedbackResultCategory.Phishing]: t(
          'operations.euq.analysis.completedPhishing'
        ),
        [FeedbackResultCategory.Flagged]: t(
          'operations.euq.analysis.completedFlagged'
        ),
        [FeedbackResultCategory.Normal]: t(
          'operations.euq.analysis.completedNotARisk'
        ),
        [FeedbackResultCategory.InProgress]: t(
          'operations.euq.analysis.inProgress'
        ),
        [FeedbackResultCategory.Error]: t('operations.euq.analysis.failed'),
      };
      tags.push({
        type: 'resultCategory',
        value: resultCategory,
        fieldLabel: t('operations.euq.analysis.result'),
        valueLabel: resultLabels[resultCategory],
      });
    }

    return tags;
  }, [keyword, status, category, resultCategory, t]);

  // Initialize filter blocks - these are the keyword filters that should be shown
  const [filterBlockList, setFilterBlockList] = useState<FilterKeywordField[]>(
    () => {
      // Show filters that have non-empty values from persisted state
      return (ADDITIONAL_FILTERS as FilterKeywordField[]).filter(
        (field) => keyword[field] && keyword[field].trim() !== ''
      );
    }
  );

  const removeBlockByField = (field: FilterKeywordField) => {
    const copyArr = [...filterBlockList];
    setFilterBlockList(copyArr.filter((v) => v !== field));
    setKeyword(field, '');
  };

  const removeFilterTag = (tag: FilterTag) => {
    if (tag.type === 'keyword' && tag.field) {
      removeBlockByField(tag.field);
    } else if (tag.type === 'status' && setStatus) {
      // Reset to All which will clear the URL param
      setStatus(FeedbackFilterStatus.All);
      resetSelectionsAndPagination?.();
    } else if (tag.type === 'category' && setCategory) {
      // Reset to All which will clear the URL param
      setCategory(FeedbackFilterCategory.All);
      resetSelectionsAndPagination?.();
    } else if (tag.type === 'resultCategory' && setResultCategory) {
      // Reset to All which will clear the URL param
      setResultCategory(FeedbackResultCategory.All);
      resetSelectionsAndPagination?.();
    }
  };

  //用于menu的items
  const filterItems = useMemo(() => {
    return (ADDITIONAL_FILTERS as FilterKeywordField[]).filter(
      (v) => !filterBlockList.includes(v)
    );
  }, [filterBlockList]);

  return (
    <Flex alignItems="start" gap="2x">
      <Menu>
        <MenuToggle>
          <Button variant="secondary" gap="2x">
            <Icon as={FilterAddIcon} />
            {t('common.action.label.addFilters')}
          </Button>
        </MenuToggle>
        <MenuList minWidth="100%">
          {filterItems.map((field) => (
            <MenuItem
              key={field}
              onClick={() => {
                //append
                setFilterBlockList([...filterBlockList, field]);
              }}
            >
              {t(`operations.euq.detail.table.header.${field}`)}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      {(filterBlockList.length > 0 || filterTags.length > 0) && (
        <Flex alignItems="center" gap="2x" flexWrap="wrap" height="100%">
          {/* Display keyword filter blocks that have been added */}
          {filterBlockList.map((field) => (
            <FilterBlock
              key={field}
              value={keyword[field] || ''}
              field={field}
              removeBlockByField={removeBlockByField}
              setKeyword={setKeyword}
            />
          ))}

          {/* Display active filter tags (non-keyword filters) */}
          {filterTags
            .filter((tag) => tag.type !== 'keyword')
            .map((tag, index) => (
              <FilterTag
                key={`${tag.type}-${tag.value}-${index}`}
                field={tag.fieldLabel}
                value={tag.valueLabel}
                onRemove={() => removeFilterTag(tag)}
                maxWidth="225px"
              />
            ))}
        </Flex>
      )}
    </Flex>
  );
};

const FilterBlock = ({
  value,
  field,
  removeBlockByField,
  setKeyword,
}: {
  value: string;
  field: FilterKeywordField;
  removeBlockByField: (field: FilterKeywordField) => void;
  setKeyword: (field: FilterKeywordField, value: string) => void;
}) => {
  const { t } = useTranslation(['operations', 'common']);
  const [inputValue, setInputValue] = useState(value);

  // Update local input value when the prop value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  return (
    <Popover>
      {({ onClose, onOpen }: any) => (
        <>
          <PopoverTrigger>
            <Box>
              <FilterTag
                field={t(`operations.euq.detail.table.header.${field}`)}
                value={value}
                onRemove={() => removeBlockByField(field)}
                onClick={onOpen}
                isClickable={true}
                maxWidth="225px"
              />
            </Box>
          </PopoverTrigger>
          <PopoverContent>
            <PopoverBody>
              <Flex direction="column" gap="2x">
                <Input
                  placeholder=""
                  value={inputValue}
                  onChange={(e: any) => setInputValue(e.target.value)}
                  maxLength={321}
                />
                <Box ml="auto">
                  <Button
                    variant="primary"
                    onClick={() => {
                      setKeyword(field, inputValue);
                      onClose();
                    }}
                  >
                    {t('common.button.apply')}
                  </Button>
                </Box>
              </Flex>
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
};

export default AddFilterMenu;
